import { useState } from 'react';
import axios from 'axios';
import { generateCredentialsPDF, generateCredentialsExcel } from '@/utils/credentialsExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Centralizes all merchant-related HTTP calls used by MerchantManagementTab.
 * Each method shows toast feedback and triggers onReload on success.
 */
export default function useMerchantActions({ setToast, onReload }) {
  const [sendingEmail, setSendingEmail] = useState(null);

  const createMerchant = async (data) => {
    if (!data.email || !data.password || !data.company_name) {
      setToast({ type: 'error', message: 'Email, contrasena y nombre de empresa son obligatorios' });
      return null;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/super-admin/merchants/create`, data);
      setToast({ type: 'success', message: 'Comercio creado exitosamente' });
      onReload();
      return res.data;
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error al crear comercio' });
      return null;
    }
  };

  const downloadCredentials = async (userId, format) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchants/${userId}/credentials`);
      const exportData = {
        merchant: res.data.merchant,
        project: res.data.projects?.[0] || null,
        api_keys: res.data.api_keys,
        environments: res.data.environments,
      };
      if (format === 'pdf') generateCredentialsPDF(exportData);
      else generateCredentialsExcel(exportData);
      setToast({ type: 'success', message: `${format.toUpperCase()} descargado` });
    } catch (error) {
      setToast({ type: 'error', message: `Error al descargar credenciales: ${error.message || 'desconocido'}` });
    }
  };

  const resendEmail = async (userId, email) => {
    setSendingEmail(userId);
    try {
      await axios.post(`${BACKEND_URL}/api/super-admin/merchants/${userId}/resend-email`, null);
      setToast({ type: 'success', message: `Email reenviado a ${email}` });
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error al reenviar email' });
    } finally {
      setSendingEmail(null);
    }
  };

  const downloadGuide = async (userId, format) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/super-admin/merchants/${userId}/integration-guide?format=${format}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `CauraPay_Guia.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setToast({ type: 'success', message: `Guia ${format.toUpperCase()} descargada` });
    } catch (error) {
      setToast({ type: 'error', message: `Error al descargar guia: ${error.message || 'desconocido'}` });
    }
  };

  const updateStatus = async (userId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/super-admin/merchants/${userId}/status`,
        null,
        { params: { new_status: newStatus } },
      );
      setToast({ type: 'success', message: 'Estado actualizado correctamente' });
      onReload();
      return true;
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error' });
      return false;
    }
  };

  const updateFee = async (userId, newFee, reason) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/super-admin/merchants/${userId}/fee`,
        null,
        { params: { new_fee_percentage: parseFloat(newFee), reason: reason || undefined } },
      );
      setToast({ type: 'success', message: 'Comision actualizada' });
      onReload();
      return true;
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error' });
      return false;
    }
  };

  const updateOverpayTolerance = async (userId, toleranceVes, reason) => {
    try {
      const value = toleranceVes === '' || toleranceVes === null ? 0 : parseFloat(toleranceVes);
      await axios.put(
        `${BACKEND_URL}/api/super-admin/merchants/${userId}/overpay-tolerance`,
        null,
        { params: { tolerance_ves: value, reason: reason || undefined } },
      );
      setToast({
        type: 'success',
        message: value ? `Tolerancia de sobrepago: +${value} Bs` : 'Tolerancia de sobrepago desactivada',
      });
      onReload();
      return true;
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error' });
      return false;
    }
  };

  const resetPassword = async (userId, newPassword) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/super-admin/merchants/${userId}/password`,
        null,
        { params: { new_password: newPassword } },
      );
      setToast({ type: 'success', message: 'Contrasena actualizada' });
      return true;
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.detail || 'Error' });
      return false;
    }
  };

  const loadConcentrator = async (userId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchants/${userId}/concentrator`);
      return res.data.concentrator || {};
    } catch {
      setToast({ type: 'error', message: 'No se pudo cargar la concentradora' });
      return null;
    }
  };

  const saveConcentrator = async (userId, concentrator) => {
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/super-admin/merchants/${userId}/concentrator`,
        concentrator,
      );
      setToast({ type: 'success', message: res.data.message || 'Concentradora actualizada' });
      onReload();
      return true;
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.detail || 'Error al guardar concentradora' });
      return false;
    }
  };

  const loadDeletionImpact = async (userId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchants/${userId}/deletion-impact`);
      return res.data;
    } catch {
      setToast({ type: 'error', message: 'No se pudo cargar el impacto de eliminación' });
      return null;
    }
  };

  const deleteMerchant = async (userId, payload) => {
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/super-admin/merchants/${userId}`, { data: payload });
      setToast({ type: 'success', message: res.data.message || 'Comercio eliminado' });
      onReload();
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setToast({
        type: 'error',
        message: typeof detail === 'string' ? detail : (detail?.message || 'Error al eliminar comercio'),
      });
      return false;
    }
  };

  return {
    sendingEmail,
    createMerchant, downloadCredentials, resendEmail, downloadGuide,
    updateStatus, updateFee, updateOverpayTolerance, resetPassword,
    loadConcentrator, saveConcentrator,
    loadDeletionImpact, deleteMerchant,
  };
}
